/**
 * Walking a stored Delta into LINES, and deriving section numbers from them.
 *
 * Pure: no React, no Quill, no DOM, and `@platform/shared-types` is imported
 * TYPE-ONLY so this file loads under the package's standalone `vitest run`
 * (which has no Vite and no alias — see the note in `quillDelta.ts`).
 *
 * ── THE WALK, AND WHY THE OBVIOUS ONE IS WRONG ───────────────────────────
 *
 * A line attribute rides on the NEWLINE THAT TERMINATES THE LINE, never on
 * the text op:
 *
 *   WRONG   { insert: 'Term\n', attributes: { header: 2 } }
 *   RIGHT   { insert: 'Term' }, { insert: '\n', attributes: { header: 2 } }
 *
 * And consecutive unattributed lines COLLAPSE INTO ONE OP — Quill emits
 * `{ insert: 'a\n\nb\n' }`, not three ops. So lines must be split on `\n`
 * ACROSS the op stream; a walk of one line per op fuses three paragraphs into
 * one and loses every heading in a contract while the text survives.
 *
 * This mirrors `packages/documents-render/src/delta.ts`, which does the same
 * walk for the two renderers. It is duplicated rather than imported because
 * `documents-render` is not a dependency of this source-only package — but
 * the NUMBERING RULE below must stay identical to
 * `computeDeltaHeadingNumbers` there, or the editor prints numbers the signed
 * PDF does not agree with.
 */
import type { DeltaDocument, DeltaOp, NumberingStyle } from '@platform/shared-types';

/** The block-embed keys — those that stand BETWEEN lines rather than inside one. */
const BLOCK_EMBED_KEYS = ['pageBreak', 'callout', 'table', 'signatureField', 'mergeFieldBlock'];

function isBlockEmbed(insert: unknown): boolean {
  return !!insert && typeof insert === 'object'
    && BLOCK_EMBED_KEYS.some((k) => k in (insert as Record<string, unknown>));
}

/** Line attributes we care about here. Anything else is irrelevant to an outline. */
export interface OutlineLineAttrs {
  header?: 1 | 2 | 3;
  anchorId?: string;
}

export interface DeltaLineInfo {
  /** Plain text of the line. An inline embed contributes its token text. */
  text: string;
  attrs: OutlineLineAttrs;
  /** Character index of the line's FIRST character in the Delta, for `setSelection`/scroll. */
  index: number;
  /** True for a block embed standing on its own between lines. */
  isBlock: boolean;
}

/** The text one embed contributes to its line. Mirrors `deltaPlainText`. */
function embedText(insert: Record<string, any>): string {
  if ('mergeField' in insert) return `{{${insert.mergeField?.name ?? ''}}}`;
  if ('docRef' in insert) return String(insert.docRef?.text ?? '');
  return '';
}

/**
 * Every line in the document, in order, with the character index it starts at.
 *
 * An op's length in Quill's index space is `insert.length` for a string and
 * exactly `1` for any embed — that is what makes `index` usable directly with
 * `quill.setSelection`.
 */
export function walkDeltaLines(delta: DeltaDocument | null | undefined): DeltaLineInfo[] {
  const lines: DeltaLineInfo[] = [];
  if (!delta || !Array.isArray(delta.ops)) return lines;

  let text = '';
  let start = 0;
  let cursor = 0;

  const closeLine = (attrs: OutlineLineAttrs, isBlock = false) => {
    lines.push({ text, attrs, index: start, isBlock });
    text = '';
  };

  for (const op of delta.ops as DeltaOp[]) {
    const insert: any = (op as any).insert;

    if (typeof insert === 'string') {
      // The attributes of a `\n`-bearing op apply to every line it terminates.
      const attrs = ((op as any).attributes ?? {}) as OutlineLineAttrs;
      let seg = '';
      for (let i = 0; i < insert.length; i += 1) {
        const ch = insert[i];
        if (ch === '\n') {
          text += seg;
          seg = '';
          closeLine(attrs);
          start = cursor + i + 1;
        } else {
          seg += ch;
        }
      }
      text += seg;
      cursor += insert.length;
      continue;
    }

    if (insert && typeof insert === 'object') {
      if (isBlockEmbed(insert)) {
        // A block embed occupies its own line. Anything buffered before it
        // belongs to the line it interrupted, which Quill terminates itself.
        lines.push({ text: '', attrs: {}, index: cursor, isBlock: true });
        start = cursor + 1;
      } else {
        text += embedText(insert);
      }
      cursor += 1;
    }
  }

  // A trailing fragment with no terminating newline is not a line Quill would
  // produce, but a hand-written Delta can contain one — keep it rather than
  // silently dropping words.
  if (text !== '') closeLine({});

  return lines;
}

export interface OutlineHeading {
  level: 1 | 2 | 3;
  text: string;
  anchorId?: string;
  /** `"2."`, `"2.1"`, or `''` when this heading is not numbered. */
  number: string;
  /** Character index of the heading line, for scroll-to. */
  index: number;
}

/**
 * Headings with their DERIVED section numbers.
 *
 * THE RULE, identical to `computeDeltaHeadingNumbers` in documents-render:
 * level 1 is the document title and is never numbered; level 2 is `N.`; level
 * 3 is `N.M` and only under the `legal` style. A heading with no `anchorId`
 * still ADVANCES the counters — numbering is a property of position, so an
 * unreferenced heading must not make the ones after it shift.
 *
 * `style` defaults to `'none'` to match `RenderMeta.numbering`, so the editor
 * shows a number only where the renderer would actually print one. Showing
 * numbers the artifact does not carry would be a lie in the one place a lie
 * costs the most.
 */
export function deltaOutline(
  delta: DeltaDocument | null | undefined,
  style: NumberingStyle = 'none',
): OutlineHeading[] {
  const out: OutlineHeading[] = [];
  let major = 0;
  let minor = 0;

  for (const line of walkDeltaLines(delta)) {
    const level = line.attrs.header;
    if (level === undefined) continue;

    let number = '';
    if (style !== 'none' && level !== 1) {
      if (level === 2) {
        major += 1;
        minor = 0;
        number = `${major}.`;
      } else if (style === 'legal') {
        minor += 1;
        number = `${major}.${minor}`;
      }
    }

    out.push({
      level,
      text: line.text,
      anchorId: line.attrs.anchorId,
      number,
      index: line.index,
    });
  }

  return out;
}

/**
 * LEGACY CALLOUT EMBEDS → EDITABLE CALLOUT LINES.
 *
 * A stored `{ insert: { callout: { tone, content } } }` is an ATOMIC object.
 * The editor cannot let an author type inside one: the browser happily accepts
 * the keystrokes, but the Delta reads the callout's text back from the embed's
 * own value, so the edit is shown and then silently dropped on save. Losing an
 * author's words in a contract without saying so is the worst thing this
 * editor could do.
 *
 * So on the way IN, every callout embed becomes a run of ordinary lines
 * wearing `callout: <tone>`. Both encodings render identically in HTML and in
 * the PDF (verified by the renderers' own tests), so nothing about the
 * document changes — it just becomes typeable.
 *
 * Applied for EDITING ONLY. Stored documents are untouched until an author
 * saves, at which point the line form is what gets written.
 */
export function inlineCalloutEmbeds(
  delta: DeltaDocument | null | undefined,
): DeltaDocument | null | undefined {
  if (!delta || !Array.isArray(delta.ops)) return delta;
  if (!delta.ops.some((op: any) => op?.insert?.callout)) return delta;

  const out: any[] = [];
  for (const op of delta.ops as any[]) {
    const callout = op?.insert?.callout;
    if (!callout) { out.push(op); continue; }

    const content = Array.isArray(callout.content) ? callout.content : [];
    const tone = callout.tone ?? 'info';
    // The embed's inline ops become this line's content verbatim — merge
    // chips and cross-references inside an aside survive as embeds, which a
    // conversion through plain text would have flattened into literal text.
    for (const inner of content) out.push(inner);
    out.push({ insert: '\n', attributes: { callout: tone } });
  }

  /*
   * The embed carried its OWN terminating newline, which is now a stray blank
   * line after every converted aside. Dropping it keeps the converted
   * document identical in shape to the one that went in.
   */
  const cleaned: any[] = [];
  for (let i = 0; i < out.length; i += 1) {
    const prev = cleaned[cleaned.length - 1];
    const cur = out[i];
    if (
      prev?.attributes?.callout && typeof cur?.insert === 'string'
      && cur.insert.startsWith('\n') && cur.attributes === undefined
    ) {
      const rest = cur.insert.slice(1);
      if (rest !== '') cleaned.push({ insert: rest });
      continue;
    }
    cleaned.push(cur);
  }

  const last = cleaned[cleaned.length - 1];
  if (!(typeof last?.insert === 'string' && last.insert.endsWith('\n'))) {
    cleaned.push({ insert: '\n' });
  }
  return { ops: cleaned } as DeltaDocument;
}

/** The document's plain text, for the word-level diff. Mirrors `deltaPlainText`. */
export function outlinePlainText(delta: DeltaDocument | null | undefined): string {
  return walkDeltaLines(delta).map((l) => l.text).join('\n');
}
